import { Flex, Tabs, type TabsProps } from 'antd';
import type { FC } from 'react';
import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';
import styles from '../analytics.module.scss';
import { PeriodShortSelector } from '@shared/ui/selector/period-short-select/period-short-select';
import { useTranslation } from 'react-i18next';
import { AnalyticsFilters } from './analytics-filters/analytics-filters';

export type PeriodPreset = 'today' | 'yesterday' | 'week' | 'month';
export type AnalyticsView = 'overall' | 'widget';

type Props = {
  view: AnalyticsView;
  onViewChange: (next: AnalyticsView) => void;

  channelValues?: number[];
  onChannelChange: (values: number[]) => void;

  pageUrlPatterns: string[];
  onPageUrlPatternsChange: (values: string[]) => void;

  periodValue: FilterOption<DateFilterValue>[] | null;
  onPeriodChange: (val: FilterOption<DateFilterValue>[]) => void;

  periodPreset: PeriodPreset;
  onPeriodPresetChange: (next: PeriodPreset) => void;
};

export const AnalyticsControls: FC<Props> = ({
  view,
  onViewChange,
  channelValues,
  onChannelChange,
  pageUrlPatterns,
  onPageUrlPatternsChange,
  periodValue,
  onPeriodChange,
  periodPreset,
  onPeriodPresetChange,
}) => {
  const { t } = useTranslation('translation');

  const periodTabs: TabsProps['items'] = [
    { key: 'today', label: t('common.date.today') },
    { key: 'yesterday', label: t('common.date.yesterday') },
    { key: 'week', label: t('common.calendar.lastWeek') },
    { key: 'month', label: t('common.calendar.lastMonth') },
  ];

  const viewTabs: TabsProps['items'] = [
    { key: 'overall', label: t('analytics.views.overall') },
    { key: 'widget', label: t('analytics.views.widget') },
  ];

  return (
    <Flex align="center" gap={12} justify="space-between" className={styles.controlsRow}>
      <Flex align="center" gap={16} className={styles.controlsTabs}>
        <Tabs
          className={styles.analyticsTabs}
          activeKey={view}
          onChange={(key) => onViewChange(key as AnalyticsView)}
          items={viewTabs}
        />

        <div className={styles.divider} />

        <Tabs
          className={styles.analyticsTabs}
          activeKey={periodPreset}
          onChange={(key) => onPeriodPresetChange(key as PeriodPreset)}
          items={periodTabs}
        />
      </Flex>

      <Flex align="center" gap={12} className={styles.controlsRight}>
        <AnalyticsFilters
          view={view}
          channelValues={channelValues}
          onChannelChange={onChannelChange}
          pageUrlPatterns={pageUrlPatterns}
          onPageUrlPatternsChange={onPageUrlPatternsChange}
        />

        <div className={styles.divider} />

        <PeriodShortSelector
          value={periodValue}
          onChange={onPeriodChange}
          containerStyles={{ height: 26 }}
        />
      </Flex>
    </Flex>
  );
};
